// services/emailService.js
import { reportService2 } from './reportService2';
import { message, Modal } from 'antd';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://tu-api';

export const emailService = {
  /**
   * Obtiene la plantilla de correo según el hotel e idioma
   */
  async getEmailTemplate({ codigoHotel, idioma, tipo = 'FirmaCorreo' }) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          codigoHotel,
          idioma,
          tipo
        })
      });

      if (!response.ok) {
        throw new Error('Error al obtener la plantilla de correo');
      }

      const data = await response.json();
      return data.template || this.getDefaultTemplate(idioma);
    } catch (error) {
      console.error('Error obteniendo plantilla:', error);
      return this.getDefaultTemplate(idioma);
    }
  },

  /**
   * Plantilla por defecto
   */
  getDefaultTemplate(idioma) {
    const templates = {
      'CAS': {
        asunto: 'Confirmación de Reserva',
        cuerpo: `
          <br><br>
          Distinguido Sr. / Sra ###NombreReserva###,
          <br>
          Muchas gracias por haber reservado con nosotros.
          <br>
          Adjunto encontrará su confirmación de reserva.
          <br>
          Quedamos a su disposición para cualquier aclaración.
          <br>
          Saludos cordiales.
          <br><br><br>
          ###Firma###
        `
      },
      'ENG': {
        asunto: 'Reservation Confirmation',
        cuerpo: `
          <br><br>
          Dear Mr. / Ms. ###NombreReserva###,
          <br>
          Thank you for booking with us.
          <br>
          Please find attached your reservation confirmation.
          <br>
          We remain at your disposal for any questions.
          <br>
          Best regards.
          <br><br><br>
          ###Firma###
        `
      },
      'FRA': {
        asunto: 'Confirmation de Réservation',
        cuerpo: `
          <br><br>
          Cher Monsieur / Madame ###NombreReserva###,
          <br>
          Merci d'avoir réservé avec nous.
          <br>
          Veuillez trouver ci-joint votre confirmation de réservation.
          <br>
          Nous restons à votre disposition pour toute question.
          <br>
          Cordialement.
          <br><br><br>
          ###Firma###
        `
      }
    };

    return templates[idioma] || templates['CAS'];
  },

  // services/emailService.js

  /**
   * Reemplaza variables en la plantilla
   */
  replaceTemplateVariables(template, data) {
    // Si template es un string (HTML), usarlo directamente
    let cuerpo = typeof template === 'string' ? template : template.cuerpo || '';
    
    // Si no hay cuerpo, retornar vacío
    if (!cuerpo) {
      console.warn('Plantilla vacía o inválida');
      return '';
    }

    // Reemplazar variables comunes (con diferentes formatos)
    const replacements = {
      // Mayúsculas (formato original)
      '###NombreReserva###': data.nombreReserva || '',
      '###NumReserva###': data.numReserva || '',
      '###Hotel###': data.hotel || '',
      '###LinkPuerta###': data.linkPuerta || '',
      '###Firma###': data.firma || '',
      '###Fecha###': data.fecha || new Date().toLocaleDateString(),
      '###Hora###': data.hora || new Date().toLocaleTimeString(),
      '###CodigoHotel###': data.codigoHotel || '',
      
      // Minúsculas (para compatibilidad con el HTML)
      '###linkpuerta###': data.linkPuerta || '',
      '###nombrereserva###': data.nombreReserva || '',
      '###numreserva###': data.numReserva || '',
      '###hotel###': data.hotel || '',
      '###firma###': data.firma || '',
      '###fecha###': data.fecha || new Date().toLocaleDateString(),
      '###hora###': data.hora || new Date().toLocaleTimeString(),
      
      // Sin formato (por si acaso)
      '{{NombreReserva}}': data.nombreReserva || '',
      '{{NumReserva}}': data.numReserva || '',
      '{{Hotel}}': data.hotel || '',
      '{{LinkPuerta}}': data.linkPuerta || '',
      '{{Firma}}': data.firma || '',
      '{{Fecha}}': data.fecha || new Date().toLocaleDateString(),
      '{{Hora}}': data.hora || new Date().toLocaleTimeString()
    };

    // Reemplazar todas las variables
    Object.keys(replacements).forEach(key => {
      if (replacements[key] !== undefined && replacements[key] !== null) {
        cuerpo = cuerpo.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacements[key]);
      }
    });

    return cuerpo;
  },
  /**
   * Envía correo de confirmación
   */
  async sendConfirmationEmail({
    numReserva,
    codigoHotel,
    idioma,
    correoDestino,
    nombreReserva,
    pdfBase64,
    linkPuerta = '',
    firma = '',
    asuntoPersonalizado = null
  }) {
    try {
      // Validar datos
      if (!numReserva || !codigoHotel || !idioma || !correoDestino) {
        throw new Error('Faltan datos requeridos para enviar el correo');
      }

      // Obtener plantilla
      const template = await this.getEmailTemplate({ 
        codigoHotel, 
        idioma 
      });

      // console.log(" template => ",template);
      

      // Obtener firma del usuario si no se proporcionó
      if (!firma) {
        firma = await this.getUserSignature();
      }

      // Preparar datos para la plantilla
      const templateData = {
        nombreReserva,
        numReserva,
        hotel: await this.getHotelName(codigoHotel),
        linkPuerta,
        firma,
        fecha: new Date().toLocaleDateString(),
        hora: new Date().toLocaleTimeString()
      };

      // Reemplazar variables
      const cuerpo = this.replaceTemplateVariables(template, templateData);
      const asunto = asuntoPersonalizado || template.asunto || `Confirmación Reserva ${numReserva}`;

      // Preparar el PDF si viene en base64
      let pdfBlob = null;
      if (pdfBase64) {
        pdfBlob = this.base64ToBlob(pdfBase64, 'application/pdf');
      }

      // Enviar correo
      const formData = new FormData();
      formData.append('para', correoDestino);
      formData.append('asunto', asunto);
      formData.append('cuerpo', cuerpo);
      formData.append('codigoHotel', codigoHotel);
      formData.append('numReserva', numReserva);
      formData.append('idioma', idioma);
      
      if (pdfBlob) {
        formData.append('adjunto', pdfBlob, `Reserva_${numReserva}.pdf`);
      }

      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al enviar el correo');
      }

      // Registrar en histórico
      await this.registerEmailHistory({
        codigoHotel,
        numReserva,
        destino: correoDestino,
        asunto,
        adjunto: `Reserva_${numReserva}.pdf`,
        identificador: `ConfirmacionReserva${codigoHotel}_${numReserva}`
      });

      return {
        success: true,
        message: 'Correo enviado exitosamente'
      };

    } catch (error) {
      console.error('Error enviando correo:', error);
      throw error;
    }
  },

  /**
   * Proceso completo: generar reporte y enviar por correo
   */
  async generateAndSendConfirmation({
    numReserva,
    codigoHotel,
    idioma,
    correoDestino,
    nombreReserva,
    linkPuerta = '',
    firma = '',
    ver = 0 // 0 = enviar correo, 1 = solo generar PDF
  }) {
    const hideLoading = message.loading('Generando confirmación...', 0);

    try {
      // Validar datos
      if (!numReserva || !codigoHotel) {
        throw new Error('Número de reserva o código de hotel no válido');
      }

      // 1. Generar el reporte PDF
      const sqlQuery = `SELECT * FROM Reservas WHERE NumReserva = ${numReserva}`;
      
      
      // Generar reporte
      const reportResult = await reportService2.generateReport({
        hotelCode: codigoHotel,
        reportName: 'Confirmacion Reserva '+codigoHotel,
        sqlQuery: sqlQuery
      });

      if (!reportResult.success || !reportResult.pdfBase64) {
        throw new Error('Error al generar el PDF de confirmación');
      }

      // Si solo queremos generar el PDF (ver = 1)
      if (ver === 1) {
        hideLoading();
        // Mostrar el PDF
        reportService2.displayPDF(reportResult.pdfBase64);
        return {
          success: true,
          pdfBase64: reportResult.pdfBase64,
          message: 'PDF generado exitosamente'
        };
      }

      // 2. Enviar por correo (ver = 0)
      if (!correoDestino) {
        throw new Error('La reserva no tiene correo electrónico');
      }

      // 3. Confirmar envío con el usuario
      const confirmSend = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Enviar Confirmación',
          content: `¿Enviar confirmación a ${correoDestino}?`,
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });

      if (!confirmSend) {
        hideLoading();
        return {
          success: false,
          message: 'Envío cancelado por el usuario'
        };
      }

      // 4. Enviar el correo
      hideLoading();
      const emailLoading = message.loading('Enviando correo...', 0);

      const emailResult = await this.sendConfirmationEmail({
        numReserva,
        codigoHotel,
        idioma,
        correoDestino,
        nombreReserva,
        pdfBase64: reportResult.pdfBase64,
        linkPuerta,
        firma
      });

      emailLoading();

      if (emailResult.success) {
        message.success('Correo enviado exitosamente');
        return {
          success: true,
          message: 'Confirmación enviada exitosamente'
        };
      } else {
        throw new Error(emailResult.message || 'Error al enviar el correo');
      }

    } catch (error) {
      hideLoading();
      console.error('Error en proceso de confirmación:', error);
      
      Modal.error({
        title: 'Error',
        content: error.message || 'Error al generar o enviar la confirmación'
      });
      
      throw error;
    }
  },

  // ========== MÉTODOS AUXILIARES ==========

  /**
   * Obtener nombre del hotel
   */
  async getHotelName(codigoHotel) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/hoteles/hotel/${codigoHotel}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
                

      if (response.ok) {
        const data = await response.json();
        // console.log(" data getHotelname => ",data);
        // console.log(" data getHotelname => ",data.data[0].Nombre);
        
        return data.data[0].Nombre || 'Hotel';
      }
      return 'Hotel';
    } catch (error) {
      return 'Hotel';
    }
  },

  /**
   * Obtener firma del usuario
   */
  async getUserSignature() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.firma || '';
    } catch (error) {
      return '';
    }
  },

  /**
   * Obtener nombre del reporte según hotel
   */
  async getReportName(codigoHotel, tipoInforme) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/hoteles/informes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          codigoHotel,
          informe: tipoInforme
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.fichero || 'Confirmacion_Reserva';
      }
      return 'Confirmacion_Reserva';
    } catch (error) {
      return 'Confirmacion_Reserva';
    }
  },

  /**
   * Registrar en histórico de correos
   */
  async registerEmailHistory({
    codigoHotel,
    numReserva,
    destino,
    asunto,
    adjunto,
    identificador
  }) {
    try {
      await fetch(`${API_BASE_URL}/api/email/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          codigoHotel,
          numReserva,
          destino,
          asunto,
          adjunto,
          identificador,
          usuario: JSON.parse(localStorage.getItem('user') || '{}').usuario || 'system'
        })
      });
    } catch (error) {
      console.error('Error registrando histórico:', error);
      // No fallar si no se puede registrar
    }
  },

  /**
   * Convertir base64 a Blob
   */
  base64ToBlob(base64, type = 'application/pdf') {
    try {
      const cleanBase64 = base64.replace(/[\x00-\x1F\x7F-\x9F]/g, '').replace(/\s/g, '');
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type });
    } catch (error) {
      console.error('Error convirtiendo base64 a blob:', error);
      throw error;
    }
  }
};

// Exportar funciones individuales
export const generateAndSendConfirmation = emailService.generateAndSendConfirmation.bind(emailService);
export const sendConfirmationEmail = emailService.sendConfirmationEmail.bind(emailService);